import { api } from '@/lib/axios'
import type { LvPasteLine, LvPasteMode } from '@symphenia/shared-types'
import type { ExportRequest } from '@/lib/export-request'
import type {
  ClipboardItemRow,
  DashboardResponse,
  JournalEntry,
  OverviewResponse,
  PastePreviewRowView,
  PositionDetail,
  PositionGroup,
  PositionListResponse,
  ProjectDocumentRow,
  ProjectFileRow,
  ProjectTask,
  StorageUsage,
  TrafficLights,
  WorkStepsResponse,
} from '../types/phase-d.types'

/**
 * AP-35 Phase D — die Aufrufe von Positionen, Arbeitsschritten, Journal,
 * Aufgaben, Dateien und Dashboard.
 *
 * Alle Antworten kommen in der Form `{ data }` wie überall im Haus; die
 * Fehlerbehandlung macht `apiErrorText`, damit die Codes aus Blatt 2 11. und
 * Blatt 4 10. an **einer** Stelle Text werden.
 */

// ── Positionen (Blatt 2 12.) ────────────────────────────────────────────────

export async function fetchPositions(projectId: string, includeCancelled = false): Promise<PositionListResponse> {
  const res = await api.get<{ data: PositionListResponse }>(`/projects/${projectId}/positions`, {
    params: includeCancelled ? { includeCancelled: true } : undefined,
  })
  return res.data.data
}

export async function fetchPosition(projectId: string, positionId: string): Promise<PositionDetail> {
  const res = await api.get<{ data: PositionDetail }>(`/projects/${projectId}/positions/${positionId}`)
  return res.data.data
}

export async function createPosition(projectId: string, payload: Record<string, unknown>): Promise<PositionDetail> {
  const res = await api.post<{ data: PositionDetail }>(`/projects/${projectId}/positions`, payload)
  return res.data.data
}

export async function updatePosition(
  projectId: string,
  positionId: string,
  payload: Record<string, unknown>,
): Promise<PositionDetail> {
  const res = await api.patch<{ data: PositionDetail }>(`/projects/${projectId}/positions/${positionId}`, payload)
  return res.data.data
}

/** Nachtrag 7: Was nach dem Löschen im Papierkorb liegt — für „Rückgängig". */
export interface PositionDeleted {
  trashId: string
  positionId: string
  number: number
  name: string
}

export async function deletePosition(projectId: string, positionId: string): Promise<PositionDeleted> {
  const res = await api.delete<{ data: PositionDeleted }>(`/projects/${projectId}/positions/${positionId}`)
  return res.data.data
}

export interface PositionTrashEntry {
  id: string
  positionId: string
  number: number
  name: string
  deletedAt: string
  deletedByName: string | null
  expiresAt: string
}

export async function fetchPositionTrash(projectId: string): Promise<PositionTrashEntry[]> {
  const res = await api.get<{ data: PositionTrashEntry[] }>(`/projects/${projectId}/position-trash`)
  return res.data.data
}

/** Nachtrag 8a: ein Eintrag im Papierkorb des ganzen Projekts. */
export interface ProjectTrashEntry {
  id: string
  kind: 'POSITION' | 'BOM_LINE' | 'CONSUMABLE' | 'WORK_STEP'
  name: string
  positionId: string
  /** Bei gelöschten Positionen die alte Nummer; bei Zeilen die ihrer Position. */
  positionNumber: number | null
  positionName: string | null
  deletedAt: string
  deletedByName: string | null
  expiresAt: string
}

export async function fetchProjectTrash(projectId: string): Promise<ProjectTrashEntry[]> {
  const res = await api.get<{ data: ProjectTrashEntry[] }>(`/projects/${projectId}/trash`)
  return res.data.data
}

export async function restorePositionTrash(
  projectId: string,
  trashId: string,
): Promise<{ positionId: string; number: number; previousNumber: number }> {
  const res = await api.post<{ data: { positionId: string; number: number; previousNumber: number } }>(
    `/projects/${projectId}/position-trash/${trashId}/restore`,
  )
  return res.data.data
}

export async function changePositionStatus(
  projectId: string,
  positionId: string,
  payload: { to: string; note?: string },
): Promise<PositionDetail> {
  const res = await api.post<{ data: PositionDetail }>(
    `/projects/${projectId}/positions/${positionId}/status`,
    payload,
  )
  return res.data.data
}

export async function releasePosition(
  projectId: string,
  positionId: string,
  note?: string,
): Promise<PositionDetail> {
  const res = await api.post<{ data: PositionDetail }>(`/projects/${projectId}/positions/${positionId}/release`, {
    note,
  })
  return res.data.data
}

export async function approveSupplement(
  projectId: string,
  positionId: string,
  payload: { approvedAt?: string; note?: string },
): Promise<PositionDetail> {
  const res = await api.post<{ data: PositionDetail }>(
    `/projects/${projectId}/positions/${positionId}/supplement-approval`,
    payload,
  )
  return res.data.data
}

export async function reorderPositions(
  projectId: string,
  items: { id: string; sortOrder: number; groupId?: string | null }[],
): Promise<PositionListResponse> {
  const res = await api.post<{ data: PositionListResponse }>(`/projects/${projectId}/positions/reorder`, { items })
  return res.data.data
}

export async function renumberPositions(projectId: string): Promise<PositionListResponse> {
  const res = await api.post<{ data: PositionListResponse }>(`/projects/${projectId}/positions/renumber`, {})
  return res.data.data
}

// ── Gruppen ─────────────────────────────────────────────────────────────────

export async function createGroup(projectId: string, name: string): Promise<PositionGroup> {
  const res = await api.post<{ data: PositionGroup }>(`/projects/${projectId}/groups`, { name })
  return res.data.data
}

export async function renameGroup(projectId: string, groupId: string, name: string): Promise<PositionGroup> {
  const res = await api.patch<{ data: PositionGroup }>(`/projects/${projectId}/groups/${groupId}`, { name })
  return res.data.data
}

export async function deleteGroup(projectId: string, groupId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/groups/${groupId}`)
}

// ── Zwischenablage (Blatt 2 7.) ─────────────────────────────────────────────

export async function fetchClipboard(): Promise<ClipboardItemRow[]> {
  const res = await api.get<{ data: ClipboardItemRow[] }>('/clipboard')
  return res.data.data
}

export async function clearClipboard(): Promise<void> {
  await api.delete('/clipboard/clear')
}

export async function removeClipboardItem(id: string): Promise<ClipboardItemRow[]> {
  const res = await api.delete<{ data: ClipboardItemRow[] }>(`/clipboard/${id}`)
  return res.data.data
}

export async function copyPositions(projectId: string, positionIds: string[]): Promise<ClipboardItemRow[]> {
  const res = await api.post<{ data: ClipboardItemRow[] }>(`/projects/${projectId}/positions/copy`, { positionIds })
  return res.data.data
}

/** AP-35 J (Blatt 12 5.2): Liegen nur LV-Positionen in der Ablage, kommt statt `preview` die LV-Vorschau. */
export interface LvPasteVorschau {
  mode: LvPasteMode
  mergedName: string
  lines: LvPasteLine[]
  /** AP-37 4.15: dieselben Modi für Kalkulationspositionen. */
  source?: 'CALCULATION'
}

export async function previewPaste(
  projectId: string,
  options: Record<string, unknown>,
): Promise<{ preview?: PastePreviewRowView[]; lv?: LvPasteVorschau }> {
  const res = await api.post<{ data: { preview?: PastePreviewRowView[]; lv?: LvPasteVorschau } }>(
    `/projects/${projectId}/positions/paste?dryRun=1`,
    options,
  )
  return res.data.data
}

export async function paste(
  projectId: string,
  options: Record<string, unknown>,
): Promise<{ positionIds: string[]; hints: { position: string; code: string; detail?: string }[] }> {
  const res = await api.post<{
    data: { positionIds: string[]; hints: { position: string; code: string; detail?: string }[] }
  }>(`/projects/${projectId}/positions/paste`, options)
  return res.data.data
}

// ── Arbeitsschritte (Blatt 4 11.) ───────────────────────────────────────────

export async function fetchWorkSteps(positionId: string): Promise<WorkStepsResponse> {
  const res = await api.get<{ data: WorkStepsResponse }>(`/projects/positions/${positionId}/work-steps`)
  return res.data.data
}

export async function createWorkStep(positionId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/projects/positions/${positionId}/work-steps`, payload)
  return res.data.data
}

export async function updateWorkStep(stepId: string, payload: Record<string, unknown>) {
  const res = await api.patch(`/projects/work-steps/${stepId}`, payload)
  return res.data.data
}

/** Nachtrag 10: Löschen legt den Schritt in den Papierkorb — die Antwort nennt den Eintrag fürs Rückgängig. */
export async function deleteWorkStep(stepId: string): Promise<{ trashId: string; name: string }> {
  return (await api.delete<{ data: { trashId: string; name: string } }>(`/projects/work-steps/${stepId}`)).data.data
}

export interface WorkStepTrashEntry {
  id: string
  stepId: string
  name: string
  deletedAt: string
  deletedByName: string | null
  expiresAt: string
}

export async function fetchWorkStepTrash(positionId: string): Promise<WorkStepTrashEntry[]> {
  return (await api.get<{ data: WorkStepTrashEntry[] }>(`/projects/positions/${positionId}/work-steps/trash`)).data.data
}

export async function restoreWorkStep(trashId: string): Promise<WorkStepsResponse> {
  return (await api.post<{ data: WorkStepsResponse }>(`/projects/work-steps/trash/${trashId}/restore`)).data.data
}

/** Kopierte Schritte einfügen — vor `beforeId` oder am Ende (Nachtrag 10). */
export async function pasteWorkSteps(positionId: string, stepIds: string[], beforeId?: string): Promise<WorkStepsResponse> {
  return (await api.post<{ data: WorkStepsResponse }>(`/projects/positions/${positionId}/work-steps/paste`, { stepIds, beforeId })).data.data
}

export async function changeWorkStepStatus(stepId: string, payload: { to: string; note?: string; completedAt?: string }) {
  const res = await api.post(`/projects/work-steps/${stepId}/status`, payload)
  return res.data.data
}

export async function releaseWorkSteps(
  stepIds: string[],
  note?: string,
): Promise<{ released: string[]; skipped: { id: string; code: string }[] }> {
  const res = await api.post<{ data: { released: string[]; skipped: { id: string; code: string }[] } }>(
    '/projects/work-steps/release',
    { stepIds, note },
  )
  return res.data.data
}

export async function revokeWorkStepRelease(stepId: string, reason: string) {
  const res = await api.post(`/projects/work-steps/${stepId}/release/revoke`, { reason })
  return res.data.data
}

export async function reorderWorkSteps(
  positionId: string,
  items: { id: string; sortOrder: number; phaseId?: string }[],
): Promise<WorkStepsResponse> {
  const res = await api.post<{ data: WorkStepsResponse }>(`/projects/positions/${positionId}/work-steps/reorder`, {
    items,
  })
  return res.data.data
}

// ── Journal und Aufgaben (Blatt 9 9.) ───────────────────────────────────────

export async function fetchJournal(
  projectId: string,
  params: Record<string, unknown>,
): Promise<{ data: JournalEntry[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
  const res = await api.get(`/projects/${projectId}/journal`, { params })
  return res.data
}

export async function createJournalEntry(projectId: string, payload: Record<string, unknown>): Promise<JournalEntry> {
  const res = await api.post<{ data: JournalEntry }>(`/projects/${projectId}/journal`, payload)
  return res.data.data
}

export async function updateJournalEntry(
  projectId: string,
  entryId: string,
  payload: Record<string, unknown>,
): Promise<JournalEntry> {
  const res = await api.patch<{ data: JournalEntry }>(`/projects/${projectId}/journal/${entryId}`, payload)
  return res.data.data
}

export async function deleteJournalEntry(projectId: string, entryId: string): Promise<void> {
  await api.delete(`/projects/${projectId}/journal/${entryId}`)
}

export async function pinJournalEntry(
  projectId: string,
  entryId: string,
  payload: { color?: string; pinnedUntil?: string },
): Promise<JournalEntry> {
  const res = await api.post<{ data: JournalEntry }>(`/projects/${projectId}/journal/${entryId}/pin`, payload)
  return res.data.data
}

export async function unpinJournalEntry(projectId: string, entryId: string): Promise<JournalEntry> {
  const res = await api.post<{ data: JournalEntry }>(`/projects/${projectId}/journal/${entryId}/unpin`, {})
  return res.data.data
}

export async function journalEntryToTask(
  projectId: string,
  entryId: string,
  payload: Record<string, unknown>,
): Promise<ProjectTask> {
  const res = await api.post<{ data: ProjectTask }>(`/projects/${projectId}/journal/${entryId}/to-task`, payload)
  return res.data.data
}

export async function fetchProjectTasks(projectId: string): Promise<ProjectTask[]> {
  const res = await api.get<{ data: ProjectTask[] }>(`/projects/${projectId}/tasks`)
  return res.data.data
}

export async function createTask(projectId: string, payload: Record<string, unknown>): Promise<ProjectTask> {
  const res = await api.post<{ data: ProjectTask }>(`/projects/${projectId}/tasks`, payload)
  return res.data.data
}

export async function updateTask(taskId: string, payload: Record<string, unknown>): Promise<ProjectTask> {
  const res = await api.patch<{ data: ProjectTask }>(`/projects/tasks/${taskId}`, payload)
  return res.data.data
}

export async function completeTask(taskId: string, note?: string): Promise<ProjectTask> {
  const res = await api.post<{ data: ProjectTask }>(`/projects/tasks/${taskId}/done`, { note })
  return res.data.data
}

export async function snoozeTask(taskId: string, until: string): Promise<ProjectTask> {
  const res = await api.post<{ data: ProjectTask }>(`/projects/tasks/${taskId}/snooze`, { until })
  return res.data.data
}

export async function deleteTask(taskId: string): Promise<void> {
  await api.delete(`/projects/tasks/${taskId}`)
}

/** Phase K — Export der Aufgabenliste (Mailweg `EXPORT_PROJECT_TASKS`). */
export function taskListExportRequest(params: Record<string, unknown>, columns: { key: string; label: string }[]): ExportRequest {
  return { url: '/project-tasks/export', params: { ...params, page: undefined, limit: undefined, columns: JSON.stringify(columns) } }
}

export async function fetchTaskList(
  params: Record<string, unknown>,
): Promise<{ data: ProjectTask[]; meta: { total: number; page: number; limit: number; totalPages: number } }> {
  const res = await api.get('/project-tasks', { params })
  return res.data
}

// ── Dateien (Blatt 6 7.) ────────────────────────────────────────────────────

export async function fetchFiles(projectId: string, params: Record<string, unknown> = {}): Promise<ProjectFileRow[]> {
  const res = await api.get<{ data: ProjectFileRow[] }>(`/projects/${projectId}/files`, { params })
  return res.data.data
}

export async function uploadFile(
  projectId: string,
  datei: File,
  felder: Record<string, string | boolean | undefined>,
): Promise<ProjectFileRow> {
  const form = new FormData()
  form.append('file', datei)
  for (const [schluessel, wert] of Object.entries(felder)) {
    if (wert !== undefined && wert !== null) form.append(schluessel, String(wert))
  }
  const res = await api.post<{ data: ProjectFileRow }>(`/projects/files?projectId=${projectId}`, form)
  return res.data.data
}

export async function updateFile(fileId: string, payload: Record<string, unknown>): Promise<ProjectFileRow> {
  const res = await api.patch<{ data: ProjectFileRow }>(`/projects/files/${fileId}`, payload)
  return res.data.data
}

export async function deleteFile(fileId: string): Promise<void> {
  await api.delete(`/projects/files/${fileId}`)
}

export function fileContentUrl(fileId: string): string {
  return `/api/v1/projects/files/${fileId}/content`
}

export async function fetchStorageUsage(): Promise<StorageUsage> {
  const res = await api.get<{ data: StorageUsage }>('/projects/storage-usage')
  return res.data.data
}

export async function fetchProjectDocuments(projectId: string): Promise<ProjectDocumentRow[]> {
  const res = await api.get<{ data: ProjectDocumentRow[] }>(`/projects/${projectId}/documents`)
  return res.data.data
}

// ── Dashboard und Übersicht (Blatt 8 8.) ────────────────────────────────────

export async function fetchDashboard(params: Record<string, unknown> = {}): Promise<DashboardResponse> {
  const res = await api.get<{ data: DashboardResponse }>('/project-management/dashboard', { params })
  return res.data.data
}

export async function fetchOverview(projectId: string): Promise<OverviewResponse> {
  const res = await api.get<{ data: OverviewResponse }>(`/project-management/projects/${projectId}/overview`)
  return res.data.data
}

export async function fetchTrafficLights(projectId: string): Promise<TrafficLights> {
  const res = await api.get<{ data: TrafficLights }>(`/project-management/projects/${projectId}/traffic-lights`)
  return res.data.data
}

export async function fetchPinboard(): Promise<JournalEntry[]> {
  const res = await api.get<{ data: JournalEntry[] }>('/project-management/pinboard')
  return res.data.data
}
